import { useMemo } from 'react';
import { ForecastModel } from '@app/common/static-data/models';
import { PlottableForecast, VariableId, ModelId } from '../types';
import { VARIABLES } from '@app/components/common/constants';

interface ModelVariableValidation {
  hasAnyData: boolean;
  modelsWithData: ForecastModel[];
  modelsWithoutData: ForecastModel[];
  isLoading: boolean;
  hasNoForecastData: boolean;
}

export function useModelVariableValidation(
  forecastData: Record<ModelId, PlottableForecast>,
  selectedModels: ForecastModel[],
  selectedParam: VariableId,
  loading: boolean,
): ModelVariableValidation {
  return useMemo(() => {
    const modelsWithData: ForecastModel[] = [];
    const modelsWithoutData: ForecastModel[] = [];

    // Early returns for loading states
    if (loading) {
      return {
        hasAnyData: false,
        modelsWithData: [],
        modelsWithoutData: [],
        isLoading: true,
        hasNoForecastData: false,
      };
    }

    if (Object.keys(forecastData).length === 0) {
      return {
        hasAnyData: false,
        modelsWithData: [],
        modelsWithoutData: selectedModels,
        isLoading: false,
        hasNoForecastData: true,
      };
    }

    if (!selectedModels.length || !selectedParam) {
      return {
        hasAnyData: false,
        modelsWithData: [],
        modelsWithoutData: selectedModels,
        isLoading: false,
        hasNoForecastData: false,
      };
    }

    const variable = VARIABLES.find((v) => v.id === selectedParam);
    if (!variable) {
      return {
        hasAnyData: false,
        modelsWithData: [],
        modelsWithoutData: selectedModels,
        isLoading: false,
        hasNoForecastData: false,
      };
    }

    // Check each model for variable support
    selectedModels.forEach((modelId) => {
      const modelData = forecastData[modelId];
      if (!modelData || !modelData.data) {
        modelsWithoutData.push(modelId);
        return;
      }

      // Use type assertion to handle dynamic variable types
      const variableData = (modelData.data as any)[variable.juaId];
      const hasData =
        variableData &&
        Array.isArray(variableData) &&
        variableData.some(
          (value: any) => value !== null && value !== undefined && !isNaN(Number(value)),
        );

      if (hasData) {
        modelsWithData.push(modelId);
      } else {
        modelsWithoutData.push(modelId);
      }
    });

    return {
      hasAnyData: modelsWithData.length > 0,
      modelsWithData,
      modelsWithoutData,
      isLoading: false,
      hasNoForecastData: false,
    };
  }, [forecastData, selectedModels, selectedParam, loading]);
}
